import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../../components/ui/dialog";
import { Label } from "../../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { MultiSelect } from "../../../../../components/ui/multi-select";
import { Button } from "../../../../../components/ui/button";
import { QrCode } from "lucide-react";
import { Input } from "../../../../../components/ui/input";
import { Date as DateInput } from "../../../../../components/ui/date";

interface AddNewProductionProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPipeSize: number;
  project: any;
}

interface Material {
  id: string;
  category: string;
  pipeId: string;
  status: string;
  forgeSerialNumber?: string;
  pipeNo?: string;
}

interface Specification {
  id: string;
  type: string;
  pipeId: string;
  fileName: string;
}

export const AddNewProduction: React.FC<AddNewProductionProps> = ({
  isOpen,
  onClose,
  selectedPipeSize,
  project
}) => {
  // State for form fields
  const [assemblyNumber, setAssemblyNumber] = useState<string>('');
  const [selectedWPS, setSelectedWPS] = useState<string>('');
  const [selectedDrawing, setSelectedDrawing] = useState<string>('');
  const [selectedGasIds, setSelectedGasIds] = useState<string[]>([]);
  const [selectedWireIds, setSelectedWireIds] = useState<string[]>([]);
  const [selectedForgeId, setSelectedForgeId] = useState<string>('');
  const [selectedPipeId, setSelectedPipeId] = useState<string>('');
  const [distanceFromPipeEnd, setDistanceFromPipeEnd] = useState<string>('');
  const [productionDate, setProductionDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState<string>('');
  const [unit, setUnit] = useState<string>('metric');

  // Mock data - in real app, this would come from props or API
  const [materials, setMaterials] = useState<Material[]>([
    { id: 'forge-1', category: 'Forge', pipeId: 'pipe-1', status: 'Available', forgeSerialNumber: 'FORGE-001' },
    { id: 'forge-2', category: 'Forge', pipeId: 'pipe-1', status: 'Available', forgeSerialNumber: 'FORGE-002' },
    { id: 'pipe-1', category: 'Pipe', pipeId: 'pipe-1', status: 'Available', pipeNo: 'PIPE-001' },
    { id: 'pipe-2', category: 'Pipe', pipeId: 'pipe-1', status: 'Available', pipeNo: 'PIPE-002' },
  ]);

  const [specifications, setSpecifications] = useState<Specification[]>([
    { id: 'wps-1', type: 'WPS', pipeId: 'pipe-1', fileName: 'WPS-001.pdf' },
    { id: 'wps-2', type: 'WPS', pipeId: 'pipe-1', fileName: 'WPS-002.pdf' },
    { id: 'drawing-1', type: 'DRAWING', pipeId: 'pipe-1', fileName: 'DRAWING-001.pdf' },
    { id: 'drawing-2', type: 'DRAWING', pipeId: 'pipe-1', fileName: 'DRAWING-002.pdf' },
  ]);

  // Mock options for gas and wire
  const gasOptions = [
    { value: 'gas-1', label: 'GAS-001' },
    { value: 'gas-2', label: 'GAS-002' },
    { value: 'gas-3', label: 'GAS-003' },
  ];

  const wireLotOptions = [
    { value: 'wire-1', label: 'WIRE-001' },
    { value: 'wire-2', label: 'WIRE-002' },
    { value: 'wire-3', label: 'WIRE-003' },
  ];

  // Load settings for unit
  useEffect(() => {
    const settings = localStorage.getItem('settings');
    if (settings) {
      const settingsData = JSON.parse(settings);
      setUnit(settingsData.general?.unit || 'metric');
    }
  }, []);

  // Helper function to get unit label
  const getUnitLabel = (unit: string) => {
    return unit === 'metric' ? 'mm' : 'in';
  };

  // Handler functions
  const handleGasChange = (selectedIds: string[]) => {
    setSelectedGasIds(selectedIds);
  };

  const handleWireChange = (selectedIds: string[]) => {
    setSelectedWireIds(selectedIds);
  };

  const handleForgeChange = (value: string) => {
    setSelectedForgeId(value);
  };

  const handlePipeChange = (value: string) => {
    setSelectedPipeId(value);
  };

  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDistanceFromPipeEnd(e.target.value);
  };

  const handleQRScan = (type: 'forge' | 'pipe') => {
    // QR scanning functionality would be implemented here
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!assemblyNumber || !selectedWPS || !selectedDrawing || !selectedForgeId || !selectedPipeId) {
      alert('Please fill in all required fields');
      return;
    }

    // Create production data object
    const productionData = {
      assemblyNumber,
      wpsNumber: selectedWPS,
      drawingNumber: selectedDrawing,
      gasSerialNumbers: selectedGasIds,
      wireLotNumbers: selectedWireIds,
      forgeNumber: selectedForgeId,
      pipeNumber: selectedPipeId,
      distanceFromPipeEnd: parseFloat(distanceFromPipeEnd) || 0,
      productionDate,
      note,
      pipeSize: selectedPipeSize,
      projectId: project.project.projectId,
    };

    
    // Here you would typically make an API call to save the production data
    // For now, we'll just close the modal
    onClose();
    
    // Reset form
    setAssemblyNumber('');
    setSelectedWPS('');
    setSelectedDrawing('');
    setSelectedGasIds([]);
    setSelectedWireIds([]);
    setSelectedForgeId('');
    setSelectedPipeId('');
    setDistanceFromPipeEnd('');
    setProductionDate(new Date().toISOString().split('T')[0]);
    setNote('');
  };

  const handleClose = () => {
    // Reset form when closing
    setAssemblyNumber('');
    setSelectedWPS('');
    setSelectedDrawing('');
    setSelectedGasIds([]);
    setSelectedWireIds([]);
    setSelectedForgeId('');
    setSelectedPipeId('');
    setDistanceFromPipeEnd('');
    setProductionDate(new Date().toISOString().split('T')[0]);
    setNote('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pl-0 pr-14 pb-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Add New Production
            </DialogTitle>
            <Button
              type="submit"
              form="add-new-production-form"
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              Add Production
            </Button>
          </div>
        </DialogHeader>

        <form id="add-new-production-form" onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-3 gap-6">
            {/* Row 1: Documentation and Basic Info */}
            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Assembly Number <span className="text-hero-red">*</span></Label>
              <Input 
                type="text" 
                placeholder="Enter assembly number"
                value={assemblyNumber}
                onChange={(e) => setAssemblyNumber(e.target.value)}
                className="h-10"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">WPS Number <span className="text-hero-red">*</span></Label>
              <Select value={selectedWPS} onValueChange={setSelectedWPS}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select WPS" />
                </SelectTrigger>
                <SelectContent>
                  {specifications
                    .filter(spec => spec.type === 'WPS' && spec.pipeId === `pipe-${selectedPipeSize}`)
                    .map(wps => (
                      <SelectItem key={wps.id} value={wps.id}>
                        {wps.fileName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Drawing Number <span className="text-hero-red">*</span></Label>
              <Select value={selectedDrawing} onValueChange={setSelectedDrawing}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select drawing" />
                </SelectTrigger>
                <SelectContent>
                  {specifications
                    .filter(spec => spec.type === 'DRAWING' && spec.pipeId === `pipe-${selectedPipeSize}`)
                    .map(drawing => (
                      <SelectItem key={drawing.id} value={drawing.id}>
                        {drawing.fileName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Gas Serial Numbers</Label>
              <MultiSelect
                options={gasOptions}
                selected={selectedGasIds}
                onChange={handleGasChange}
                placeholder="Select gas serial numbers..."
                className="w-full"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Wire Lot Numbers</Label>
              <MultiSelect
                options={wireLotOptions}
                selected={selectedWireIds}
                onChange={handleWireChange}
                placeholder="Select wire lot numbers..."
                className="w-full"
              />
            </div>

            {/* Row 2: Physical Components and Measurements */}
            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Forge Number <span className="text-hero-red">*</span></Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select value={selectedForgeId} onValueChange={handleForgeChange}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select forge" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials
                        .filter(m => m.category === 'Forge' && m.pipeId === `pipe-${selectedPipeSize}` && m.status === 'Available')
                        .map(forge => (
                          <SelectItem key={forge.id} value={forge.id}>
                            {forge.forgeSerialNumber}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="shrink-0 h-10 w-10"
                  onClick={() => handleQRScan('forge')}
                  type="button"
                >
                  <QrCode className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Pipe Number <span className="text-hero-red">*</span></Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select value={selectedPipeId} onValueChange={handlePipeChange}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select pipe" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials
                        .filter(m => m.category === 'Pipe' && m.pipeId === `pipe-${selectedPipeSize}` && m.status === 'Available')
                        .map(pipe => (
                          <SelectItem key={pipe.id} value={pipe.id}>
                            {pipe.pipeNo}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="shrink-0 h-10 w-10"
                  onClick={() => handleQRScan('pipe')}
                  type="button"
                >
                  <QrCode className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Distance from Pipe End to Forge ({getUnitLabel(unit)})</Label>
              <Input 
                type="number" 
                placeholder={`Enter distance in ${getUnitLabel(unit)}`}
                min="0"
                step="1"
                value={distanceFromPipeEnd}
                onChange={handleDistanceChange}
                className="h-10"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-[12px] font-medium text-text-grey">Production Date</Label>
              <DateInput 
                type="date" 
                value={productionDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProductionDate(e.target.value)}
                className="h-10"
              />
            </div>
          </div>

          {/* Note Field - Full Width */}
          <div className="space-y-3">
            <Label className="text-[12px] font-medium text-text-grey">Note</Label>
            <textarea
              placeholder="Enter any additional notes..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full h-24 px-3 py-2 text-[13px] text-text-grey border border-[#E2E5EA] rounded-lg focus:outline-none focus:ring-1 focus:ring-hero-red focus:border-hero-red resize-none"
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};